import React, { memo, useCallback } from 'react';
import { View, FlatList, ActivityIndicator, StyleSheet } from 'react-native';
import { Text } from '@/components/common/text';
import { SVG_COMPONENTS } from '@/assets/admin';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { ThreadModerationListSkeleton } from './ThreadModerationListSkeleton';
import { ThreadModerationListItem } from './ThreadModerationListItem';
import type { CorkBoardThreadSummary } from '@/types/residents/corkBoard';

interface ThreadModerationListProps {
  threads: CorkBoardThreadSummary[];
  isLoading: boolean;
  isMutating?: boolean;
  onPin: (thread: CorkBoardThreadSummary) => void;
  onLock: (thread: CorkBoardThreadSummary) => void;
  onEditTags: (thread: CorkBoardThreadSummary) => void;
  onDelete: (thread: CorkBoardThreadSummary) => void;
  onViewResponses?: (thread: CorkBoardThreadSummary) => void;
  onEdit?: (thread: CorkBoardThreadSummary) => void;
  onClose?: (thread: CorkBoardThreadSummary) => void;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  onLoadMore?: () => void;
  hasNextPage?: boolean;
  isFetchingMore?: boolean;
}

export const ThreadModerationList: React.FC<ThreadModerationListProps> = memo(
  ({
    threads,
    isLoading,
    isMutating,
    onPin,
    onLock,
    onEditTags,
    onDelete,
    onViewResponses,
    onEdit,
    onClose,
    onRefresh,
    isRefreshing,
    onLoadMore,
    hasNextPage,
    isFetchingMore,
  }) => {
    const { redesign } = useAdminColors();

    const renderItem = useCallback(
      ({ item }: { item: CorkBoardThreadSummary }) => (
        <ThreadModerationListItem
          thread={item}
          isMutating={isMutating ?? false}
          onPin={onPin}
          onLock={onLock}
          onEditTags={onEditTags}
          onDelete={onDelete}
          onViewResponses={onViewResponses}
          onEdit={onEdit}
          onClose={onClose}
        />
      ),
      [onPin, onLock, onEditTags, onDelete, onViewResponses, onEdit, onClose, isMutating],
    );

    const keyExtractor = useCallback((item: CorkBoardThreadSummary) => item.id, []);

    const handleEndReached = useCallback(() => {
      if (hasNextPage && !isFetchingMore && onLoadMore) {
        onLoadMore();
      }
    }, [hasNextPage, isFetchingMore, onLoadMore]);

    const renderFooter = useCallback(() => {
      if (!isFetchingMore) return null;
      return (
        <View style={styles.footer}>
          <ActivityIndicator size="small" color={redesign.accent.blueBg} />
        </View>
      );
    }, [isFetchingMore, redesign.accent.blueBg]);

    if (isLoading && threads.length === 0) {
      return <ThreadModerationListSkeleton />;
    }

    if (!isLoading && threads.length === 0) {
      return (
        <View style={styles.centered}>
          <SVG_COMPONENTS.EMPTY_MESSAGE width={48} height={48} color={redesign.text.subtle} />
          <Text style={[styles.emptyTitle, { color: redesign.text.primary }]}>No threads yet</Text>
          <Text style={[styles.emptySubtitle, { color: redesign.text.secondary }]}>
            Threads created by residents will appear here for moderation.
          </Text>
        </View>
      );
    }

    return (
      <FlatList
        data={threads}
        renderItem={renderItem}
        keyExtractor={keyExtractor}
        style={styles.list}
        contentContainerStyle={styles.listContent}
        onRefresh={onRefresh}
        refreshing={isRefreshing ?? false}
        onEndReached={handleEndReached}
        onEndReachedThreshold={0.4}
        ListFooterComponent={renderFooter}
      />
    );
  },
);

ThreadModerationList.displayName = 'ThreadModerationList';

const styles = StyleSheet.create({
  list: {
    flex: 1,
  },
  listContent: {
    flexGrow: 1,
    paddingTop: 8,
    paddingBottom: 8,
  },
  centered: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 32,
    minHeight: 200,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 8,
  },
  emptySubtitle: {
    fontSize: 14,
    textAlign: 'center',
    maxWidth: 300,
  },
  footer: {
    paddingVertical: 16,
    alignItems: 'center',
  },
});
